/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import {
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useToast } from '@/hooks/use-toast';
import stripeService, { SubscriptionInfo } from '@/services/StripeService';

interface AuthContextType {
  user: User | null;
  session: { user: User } | null; // For compatibility
  loading: boolean;
  signIn: (email: string) => Promise<{ error: unknown }>;
  signOut: () => Promise<void>;
  currentUser: User | null;
  userData: Record<string, unknown> | null;
  userRole: string;
  subscriptionData: SubscriptionInfo | null;
  refreshSubscription: () => Promise<void>;
  getAllUsers: () => Promise<Record<string, unknown>[]>;
  updateUserRole: (userId: string, role: string) => Promise<void>;
  updateUserStatus: (userId: string, status: boolean) => Promise<void>;
  adminUpdateSubscription: (userId: string, subscription: Record<string, unknown>) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: unknown }>;
  loginAsTestUser: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: unknown; user: User | null }>;
  resetPassword: (email: string) => Promise<{ error: unknown }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<{ user: User } | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('user');
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionInfo | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Handle auth token from URL mapping (magic link)
    const handleAuthToken = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          // If missing email, prompt user for it
          email = window.prompt('Please provide your email for confirmation');
        }
        if (email) {
          try {
            const result = await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem('emailForSignIn');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            toast({
              title: "Sesión iniciada",
              description: `Bienvenido ${result.user.email}`,
            });
          } catch (error: unknown) {
            console.error('Error signing in with email link', error);
          }
        }
      }
    };

    handleAuthToken();

    // Set up auth state listener
    let unsubscribeSubscription: (() => void) | null = null;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setSession(currentUser ? { user: currentUser } : null);
      setUser(currentUser);
      
      if (currentUser) {
        try {
          const token = await currentUser.getIdTokenResult();
          setUserRole((token.claims.role as string) || 'user');
        } catch (e) {
          console.error("Failed to get token claims", e);
          setUserRole('user');
        }
      } else {
        setUserRole('user');
      }
      
      setLoading(false);

      // Clean up previous subscription listener
      if (unsubscribeSubscription) {
        unsubscribeSubscription();
        unsubscribeSubscription = null;
      }

      // Set up real-time subscription listener if user is logged in
      if (currentUser) {
        stripeService.initializeSubscription(currentUser.uid).catch(console.error);
        unsubscribeSubscription = stripeService.onSubscriptionChange(
          currentUser.uid,
          (data) => setSubscriptionData(data),
        );
      } else {
        setSubscriptionData(null);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeSubscription) unsubscribeSubscription();
    };
  }, [toast]);

  const signIn = async (email: string) => {
    try {
      const actionCodeSettings = {
        url: window.location.href, // This must be registered in Firebase Console
        handleCodeInApp: true
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);

      toast({
        title: "Enlace mágico enviado",
        description: "Revisa tu correo electrónico para iniciar sesión",
      });

      return { error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      toast({
        title: "Error de inicio de sesión",
        description: error instanceof Error ? error.message : "Error desconocido",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string, name?: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      if (name) {
        const { updateProfile } = await import('firebase/auth');
        await updateProfile(userCredential.user, { displayName: name });
      }

      await sendEmailVerification(userCredential.user);

      toast({
        title: "Cuenta creada",
        description: "Revisa tu correo electrónico para confirmar tu cuenta",
      });

      return { error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      toast({
        title: "Error de registro",
        description: error instanceof Error ? error.message : "Error desconocido",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);

      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión correctamente",
      });
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Error al cerrar sesión",
        description: error instanceof Error ? error.message : "Error desconocido",
        variant: "destructive",
      });
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      // The user's client ID (690431699490-ugetfj76sqi58ndcg2e1i7ud9mamqg61.apps.googleusercontent.com)
      // is typically handled automatically by Firebase config, but can be added if needed.
      
      const result = await signInWithPopup(auth, provider);
      
      toast({
        title: "Sesión iniciada con Google",
        description: `Bienvenido ${result.user.displayName || result.user.email}`,
      });

      return { error: null, user: result.user };
    } catch (error: unknown) {
      console.error('Google Sign In Error:', error);
      let errorMessage = "No se pudo iniciar sesión con Google";
      
      const fireError = error as { code?: string; message?: string };
      if (fireError.code === 'auth/popup-closed-by-user') {
        errorMessage = "El inicio de sesión fue cancelado";
      } else if (fireError.message) {
        errorMessage = fireError.message;
      }

      toast({
        title: "Error de inicio de sesión",
        description: errorMessage,
        variant: "destructive",
      });
      return { error, user: null };
    }
  };

  const refreshSubscription = useCallback(async () => {
    if (!user) return;
    const data = await stripeService.getSubscriptionData(user.uid);
    setSubscriptionData(data);
  }, [user]);

  const getAllUsers = async () => {
    // Placeholder para compatibilidad
    return [];
  };

  const updateUserRole = async (userId: string, role: string) => {
    // Placeholder para compatibilidad
  };

  const updateUserStatus = async (userId: string, status: boolean) => {
    // Placeholder para compatibilidad
  };

  const adminUpdateSubscription = async (userId: string, subscription: Record<string, unknown>) => {
    // Placeholder para compatibilidad
  };

  const loginAsTestUser = async () => {
    try {
      setLoading(true);
      const userCredential = await signInAnonymously(auth);
      const firebaseUser = userCredential.user;
      
      setUser(firebaseUser);
      setSession({ user: firebaseUser });
      
      toast({
        title: "Modo de Prueba activado",
        description: "Has iniciado sesión anónimamente para pruebas locales.",
      });
    } catch (error) {
      console.error('Anonymous sign in error:', error);
      toast({
        title: "Error en Modo de Prueba",
        description: "No se pudo iniciar sesión anónimamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await firebaseSendPasswordResetEmail(auth, email);
      toast({
        title: "Correo enviado",
        description: "Revisa tu bandeja de entrada para restablecer tu contraseña",
      });
      return { error: null };
    } catch (error: unknown) {
      console.error('Reset password error:', error);
      const err = error as Error;
      toast({
        title: "Error",
        description: err.message || "No se pudo enviar el correo de restablecimiento",
        variant: "destructive",
      });
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signOut,
    signUp,
    currentUser: user, // Compatibilidad con código existente
    userData: user ? {
      id: user.uid,
      uid: user.uid, // Firebase User uses uid, supabase uses id
      email: user.email
    } : null,
    userRole,
    subscriptionData,
    refreshSubscription,
    getAllUsers,
    updateUserRole,
    updateUserStatus,
    adminUpdateSubscription,
    loginAsTestUser,
    signInWithGoogle,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
